import React, { useState, useEffect, useCallback, useReducer } from 'react';
import { financeApi } from './services/financeApi';
import { useFinanceWebSockets } from './hooks/useWebSocket';
import { InstrumentsInHand } from './components/InstrumentsInHand';
import { DashboardOverview } from './components/DashboardOverview';
import { EmployeePerformance } from './components/EmployeePerformance';
import { LiveStats } from './components/LiveStats';
import { ForceRefreshWrapper } from './components/ForceRefreshWrapper';
import './styles/FinanceDashboard.css';

// Reducer for force-updating dashboard data
const dashboardReducer = (state, action) => {
    switch (action.type) {
        case 'UPDATE_DASHBOARD':
            console.log('🔄 Reducer: Updating dashboard data');
            return { ...action.payload, _updateId: Date.now() };
        case 'UPDATE_INSTRUMENTS':
            console.log('🔄 Reducer: Updating instruments data');
            return { ...state, instruments: { ...action.payload, _updateId: Date.now() } };
        case 'UPDATE_PERFORMANCE':
            console.log('🔄 Reducer: Updating performance data');
            return { ...state, performance: { ...action.payload, _updateId: Date.now() } };
        default:
            return state;
    }
};

export const LiveFinanceDashboard = () => {
    // Use reducer for critical data to force updates
    const [dashboardState, dispatchDashboard] = useReducer(dashboardReducer, null);
    const [dashboardData, setDashboardData] = useState(null);
    const [employeePerformance, setEmployeePerformance] = useState(null);
    const [instrumentsData, setInstrumentsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [updateCounter, setUpdateCounter] = useState(0); // Force re-render counter
    const [processedMessages, setProcessedMessages] = useState(new Set()); // For deduplication

    // Initialize WebSocket connections
    const { connectionStatuses, data } = useFinanceWebSockets();

    // Log connection status changes for debugging
    useEffect(() => {
        console.log('🔌 WebSocket Connection Status Update:', connectionStatuses);
    }, [connectionStatuses]);

    // Message deduplication function with null safety
    const generateMessageId = (wsData) => {
        try {
            // Create a unique ID based on message content with null safety
            const type = wsData?.type || 'unknown';
            const notificationType = wsData?.notification_type || 'unknown';
            const timestamp = wsData?.timestamp || Date.now();
            const dataString = wsData?.data ? JSON.stringify(wsData.data) : 'no-data';
            const dataPreview = dataString?.substring?.(0, 50) || dataString || '';

            return `${type}_${notificationType}_${timestamp}_${dataPreview}`;
        } catch (error) {
            console.warn('Error generating message ID:', error);
            return `fallback_${Date.now()}_${Math.random()}`;
        }
    };

    const isMessageAlreadyProcessed = (messageId) => {
        if (processedMessages.has(messageId)) {
            console.log(`⚠️ Duplicate message detected and skipped: ${messageId}`);
            return true;
        }

        // Add to processed messages and clean up old ones (keep last 100)
        setProcessedMessages(prev => {
            const newSet = new Set(prev);
            newSet.add(messageId);

            // Keep only the last 100 messages to prevent memory bloat
            if (newSet.size > 100) {
                const array = Array.from(newSet);
                return new Set(array.slice(-100));
            }

            return newSet;
        });

        return false;
    };

    // Individual API refresh functions for complete dashboard refresh
    const refreshMainDashboard = async () => {
        try {
            console.log('🔄 Refreshing main dashboard API...');
            const dashboard = await financeApi.getDashboardOverview();

            // Debug: Log the cheque values before and after update
            const oldChequeValue = dashboardData?.overall_statistics?.total_fee_collected?.breakdown?.cheque;
            const newChequeValue = dashboard?.overall_statistics?.total_fee_collected?.breakdown?.cheque;

            console.log('💳 Cheque values - Old:', oldChequeValue, 'New:', newChequeValue);

            // Use functional update to ensure React detects the change
            setDashboardData(prevData => {
                if (JSON.stringify(prevData) !== JSON.stringify(dashboard)) {
                    console.log('🔄 Dashboard data is different, updating state');
                    return dashboard;
                }
                console.log('⚠️ Dashboard data is the same, forcing update anyway');
                return dashboard;
            });

            // Also update using reducer to force re-render
            dispatchDashboard({ type: 'UPDATE_DASHBOARD', payload: dashboard });

            setLastUpdate(new Date()); // Force re-render
            setUpdateCounter(prev => prev + 1); // Increment counter
            console.log('✅ Main dashboard refreshed');
            return dashboard;
        } catch (error) {
            console.error('❌ Error refreshing main dashboard:', error);
            throw error;
        }
    };

    const refreshEmployeePerformance = async () => {
        try {
            console.log('🔄 Refreshing employee performance API...');
            const performance = await financeApi.getEmployeePerformance({});
            setEmployeePerformance(performance);
            setLastUpdate(new Date()); // Force re-render
            console.log('✅ Employee performance refreshed');
            return performance;
        } catch (error) {
            console.error('❌ Error refreshing employee performance:', error);
            throw error;
        }
    };

    const refreshInstrumentsInHand = async () => {
        try {
            console.log('🔄 Refreshing instruments in hand API...');
            const instruments = await financeApi.getInstrumentsInHand();

            // Debug: Log the cheque values before and after update
            const oldChequeValue = instrumentsData?.summary?.total_cheques_in_system;
            const newChequeValue = instruments?.summary?.total_cheques_in_system;

            console.log('💳 Instruments Cheques - Old:', oldChequeValue, 'New:', newChequeValue);

            // Use functional update to ensure React detects the change
            setInstrumentsData(prevData => {
                if (JSON.stringify(prevData) !== JSON.stringify(instruments)) {
                    console.log('🔄 Instruments data is different, updating state');
                    return instruments;
                }
                console.log('⚠️ Instruments data is the same, forcing update anyway');
                return instruments;
            });

            // Also update using reducer to force re-render
            dispatchDashboard({ type: 'UPDATE_INSTRUMENTS', payload: instruments });

            setLastUpdate(new Date()); // Force re-render
            setUpdateCounter(prev => prev + 1); // Increment counter
            console.log('✅ Instruments in hand refreshed');
            return instruments;
        } catch (error) {
            console.error('❌ Error refreshing instruments in hand:', error);
            throw error;
        }
    };

    // Complete dashboard refresh function
    const handleCompleteDashboardRefresh = useCallback(async (refreshData) => {
        try {
            console.log('🔄 Complete dashboard refresh initiated:', refreshData);

            // Show notification about what triggered the refresh
            const triggerReason = refreshData.trigger_reason || 'financial transaction';
            const triggerData = refreshData.trigger_data;

            let notificationMessage = `Dashboard refreshing due to: ${triggerReason}`;
            if (triggerData) {
                if (triggerReason === 'payment_received') {
                    notificationMessage = `Payment received: ₹${triggerData.amount} from ${triggerData.student_name}`;
                } else if (triggerReason === 'balance_transferred') {
                    notificationMessage = `Balance transferred: ₹${triggerData.amount}`;
                } else if (triggerReason === 'verify_cash_transfer_otp') {
                    notificationMessage = `Cash transfer verified: ₹${triggerData.amount}`;
                } else if (triggerReason === 'initiate_cash_deposit') {
                    notificationMessage = `Cash deposit made: ₹${triggerData.amount}`;
                } else if (triggerReason === 'trigger_deposit_notification') {
                    notificationMessage = `Deposit made: ₹${triggerData.amount}`;
                } else if (triggerReason === 'trigger_reconciliation_notification') {
                    notificationMessage = `Deposit reconciled: ₹${triggerData.amount}`;
                } else if (triggerData.amount) {
                    notificationMessage = `${triggerReason}: ₹${triggerData.amount}`;
                }
            }

            console.log('� Dashboard update triggered:', notificationMessage);

            // Refresh all APIs simultaneously
            console.log('🔄 Refreshing all dashboard APIs simultaneously...');
            const startTime = Date.now();

            const [newDashboard, newPerformance, newInstruments] = await Promise.all([
                refreshMainDashboard(),
                refreshEmployeePerformance(),
                refreshInstrumentsInHand()
            ]);

            const endTime = Date.now();
            console.log(`✅ All dashboard APIs refreshed in ${endTime - startTime}ms`);

            // Debug: Log final values from API responses
            console.log('🔍 Final API response values:');
            console.log('Dashboard cheque value:', newDashboard?.overall_statistics?.total_fee_collected?.breakdown?.cheque);
            console.log('Instruments cheque value:', newInstruments?.summary?.total_cheques_in_system);

            setLastUpdate(new Date());

            // Show success notification
            console.log('✅ Dashboard updated with latest data');

        } catch (error) {
            console.error('❌ Error during complete dashboard refresh:', error);
        }
    }, []);

    // Fetch initial data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [dashboard, performance, instruments] = await Promise.all([
                financeApi.getDashboardOverview(),
                financeApi.getEmployeePerformance({}),
                financeApi.getInstrumentsInHand()
            ]);

            setDashboardData(dashboard);
            setEmployeePerformance(performance);
            setInstrumentsData(instruments);
            setLastUpdate(new Date());

            console.log('✅ Finance Dashboard Data loaded successfully');
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle WebSocket messages for real-time updates
    useEffect(() => {
        // Check for dashboard updates
        if (data['/ws/finance/dashboard/']) {
            const wsData = data['/ws/finance/dashboard/'];

            // Safety check for wsData
            if (!wsData || typeof wsData !== 'object') {
                console.log('⚠️ Invalid WebSocket data received:', wsData);
                return;
            }

            console.log('📊 WebSocket dashboard update received');

            // Check for duplicate messages
            const messageId = generateMessageId(wsData);
            if (isMessageAlreadyProcessed(messageId)) {
                return; // Skip duplicate message
            }

            // Handle different possible data structures from backend
            if (wsData.type === 'dashboard_update') {

                // Handle complete dashboard refresh notification
                if (wsData.notification_type === 'complete_dashboard_refresh') {
                    console.log('🔄 Complete dashboard refresh notification received');
                    handleCompleteDashboardRefresh(wsData.data);
                    return;
                }

                // Handle specific payment notification - display in LiveStats
                if (wsData.notification_type === 'payment_received') {
                    console.log('💰 Payment received via WebSocket - displaying in live updates');
                    setLastUpdate(new Date());
                    return;
                }

                // Handle balance transfer notification
                if (wsData.notification_type === 'balance_transferred') {
                    console.log('🔄 Balance transfer via WebSocket - displaying in live updates');
                    setLastUpdate(new Date());
                    return;
                }

                // Handle cash transfer verification notification
                if (wsData.notification_type === 'verify_cash_transfer_otp') {
                    console.log('✅ Cash transfer OTP verified via WebSocket - displaying in live updates');
                    setLastUpdate(new Date());
                    return;
                }

                // Handle cash deposit notification
                if (wsData.notification_type === 'initiate_cash_deposit') {
                    console.log('💵 Cash deposit initiated via WebSocket - displaying in live updates');
                    setLastUpdate(new Date());
                    return;
                }

                // Handle deposit notification
                if (wsData.notification_type === 'trigger_deposit_notification') {
                    console.log('🏦 Deposit made via WebSocket - displaying in live updates');
                    setLastUpdate(new Date());
                    return;
                }

                // Handle reconciliation notification
                if (wsData.notification_type === 'trigger_reconciliation_notification') {
                    console.log('✅ Deposit reconciled via WebSocket - displaying in live updates');
                    setLastUpdate(new Date());
                    return;
                }

                // Legacy support for old notification types
                if (wsData.notification_type === 'deposit_made') {
                    console.log('🏦 Deposit made via WebSocket - displaying in live updates');
                    setLastUpdate(new Date());
                    return;
                }

                if (wsData.notification_type === 'deposit_reconciled') {
                    console.log('✅ Deposit reconciled via WebSocket - displaying in live updates');
                    setLastUpdate(new Date());
                    return;
                }

                // Handle structured updates if they contain dashboard sections (legacy support)
                if (wsData.data?.dashboard) {
                    console.log('💼 Updating dashboard overview data');
                    setDashboardData(wsData.data.dashboard);
                    setLastUpdate(new Date());
                }
                if (wsData.data?.performance) {
                    console.log('👥 Updating employee performance data');
                    setEmployeePerformance(wsData.data.performance);
                }
                if (wsData.data?.instruments) {
                    console.log('💰 Updating instruments in hand data');
                    setInstrumentsData(wsData.data.instruments);
                }
            } else if (wsData.dashboard || wsData.performance || wsData.instruments) {
                // Handle direct data updates (legacy support)
                if (wsData.dashboard) {
                    console.log('💼 Updating dashboard overview data (direct)');
                    setDashboardData(wsData.dashboard);
                    setLastUpdate(new Date());
                }
                if (wsData.performance) {
                    console.log('👥 Updating employee performance data (direct)');
                    setEmployeePerformance(wsData.performance);
                }
                if (wsData.instruments) {
                    console.log('💰 Updating instruments in hand data (direct)');
                    setInstrumentsData(wsData.instruments);
                }
            } else {
                // For unknown structures, just update timestamp
                console.log('🔄 Unknown WebSocket data structure - updating live display');
                setLastUpdate(new Date());
            }
        }

        // Check for notification updates
        if (data['/ws/finance/notifications/']) {
            const notification = data['/ws/finance/notifications/'];

            // Safety check for notification data
            if (!notification || typeof notification !== 'object') {
                console.log('⚠️ Invalid notification data received:', notification);
                return;
            }

            console.log('🔔 WebSocket notification received - displaying in live updates');
            console.log('🔔 Notification details:', JSON.stringify(notification, null, 2));

            // Check for duplicate messages
            const notificationMessageId = generateMessageId(notification);
            if (isMessageAlreadyProcessed(notificationMessageId)) {
                return; // Skip duplicate message
            }

            console.log(`✅ Processing unique notification: ${notificationMessageId.substring(0, 50)}...`);

            // Handle complete dashboard refresh from notifications channel
            if (notification.notification_type === 'complete_dashboard_refresh') {
                console.log('🔄 Complete dashboard refresh notification received (notifications channel)');
                handleCompleteDashboardRefresh(notification.data);
                return;
            }

            // Just update timestamp to show live activity for other notifications
            setLastUpdate(new Date());
        }
    }, [data, handleCompleteDashboardRefresh, isMessageAlreadyProcessed]);

    // Initial data fetch ONLY
    useEffect(() => {
        fetchData();
    }, []);

    // REMOVED AUTO-REFRESH - WebSockets handle real-time updates
    // No more automatic page refreshing every 30 seconds!

    const handleRefresh = () => {
        console.log('Manual refresh triggered');
        fetchData();
    };

    if (loading) {
        return (
            <div className="finance-dashboard-loading">
                <div className="loading-spinner"></div>
                <p>Loading Finance Dashboard...</p>
            </div>
        );
    }

    return (
        <div style={{
            height: "100vh",
            overflowY: "auto",
            overflowX: "hidden",
        }}>
            <div className="finance-dashboard">
                <div className="dashboard-header">
                    <div className='d-flex gap-2 align-items-start'>
                        <h1>Fee & A/c Dashboard  </h1>
                        <span className="live-indicator">● Live</span>
                    </div>

                    <div className="header-controls">
                        {/* <button
                        className="refresh-btn"
                        onClick={handleRefresh}
                        title="Refresh Dashboard"
                    >
                        🔄 Refresh
                    </button> */}
                    </div>
                </div>

                <div className="dashboard-layout mt-2">
                    <div className="main-contents">
                        {/* Dashboard Overview - Middle Section */}
                        <div className="dashboard-section">
                            <ForceRefreshWrapper refreshTrigger={`${updateCounter}-${lastUpdate.getTime()}`}>
                                <DashboardOverview
                                    key={`dashboard-${updateCounter}-${lastUpdate.getTime()}`}
                                    data={dashboardState || dashboardData}
                                />
                            </ForceRefreshWrapper>
                        </div>
                        {/* Instruments in Hand - Top Section */}
                        <div className="dashboard-section mt-2">
                            <ForceRefreshWrapper refreshTrigger={`${updateCounter}-${lastUpdate.getTime()}`}>
                                <InstrumentsInHand
                                    key={`instruments-${updateCounter}-${lastUpdate.getTime()}`}
                                    data={dashboardState?.instruments || instrumentsData}
                                />
                            </ForceRefreshWrapper>
                        </div>




                        {/* Employee Performance - Bottom Section */}
                        <div className="dashboard-section mt-2">
                            <ForceRefreshWrapper refreshTrigger={`${updateCounter}-${lastUpdate.getTime()}`}>
                                <EmployeePerformance
                                    key={`performance-${updateCounter}-${lastUpdate.getTime()}`}
                                    data={dashboardState?.performance || employeePerformance}
                                />
                            </ForceRefreshWrapper>
                        </div>
                    </div>                {/* Live Stats Sidebar */}
                    <div className="sidebar-contents">
                        <LiveStats
                            webSocketData={data}
                            connectionStatuses={connectionStatuses}
                            dashboardData={dashboardData}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
